/*
 * The Marketing tabs ported from the WordPress Steward (Dashboard, Channels, Due again, Email list, Guest posts) as
 * React Query hooks. Each change answers with its tab's new state, which goes straight into the cache.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ChannelsAnswer,
  ChannelsQuery,
  DashboardAnswer,
  DueAgainAnswer,
  EmailListAnswer,
  PitchRow,
  PitchStatus,
} from "@steward/shared";
import { api } from "./client";
import { qs } from "./hooks";

export const useMkDashboard = (companyId: string) =>
  useQuery({ queryKey: ["mk-dash", companyId], queryFn: () => api<DashboardAnswer>("GET", "marketing/dashboard") });

export const useChannels = (companyId: string, range: ChannelsQuery["range"]) =>
  useQuery({
    queryKey: ["mk-channels", companyId, range],
    queryFn: () => api<ChannelsAnswer>("GET", `marketing/channels${qs({ range })}`),
  });

/** A change whose answer replaces one cached query (by the start of its key), and refreshes the dashboard. */
function useSet<V, T>(key: string, run: (v: V) => Promise<T>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: (data) => {
      qc.setQueriesData({ queryKey: [key] }, data);
      return qc.invalidateQueries({ queryKey: ["mk-dash"] });
    },
  });
}

export const useDueAgain = (companyId: string) =>
  useQuery({ queryKey: ["mk-due", companyId], queryFn: () => api<DueAgainAnswer>("GET", "marketing/due-again") });
export const useSaveDueAgain = () =>
  useSet("mk-due", (v: { days?: Record<string, number>; text?: string }) => api<DueAgainAnswer>("PUT", "marketing/due-again", v));
export const useSkipDue = () =>
  useSet("mk-due", (v: { clientId: string; kind: string }) => api<DueAgainAnswer>("POST", "marketing/due-again/skip", v));
export const useTextDue = () =>
  useSet("mk-due", (v: { clientId: string; kind: string; text: string }) =>
    api<DueAgainAnswer>("POST", "marketing/due-again/text", v),
  );

export const useEmailList = (companyId: string) =>
  useQuery({ queryKey: ["mk-signups", companyId], queryFn: () => api<EmailListAnswer>("GET", "marketing/signups") });
export const useUnsub = () =>
  useSet("mk-signups", (v: { id: string; unsub: boolean }) =>
    api<EmailListAnswer>("PATCH", `marketing/signups/${v.id}`, { unsub: v.unsub }),
  );
export const useRemoveSignup = () =>
  useSet("mk-signups", (id: string) => api<EmailListAnswer>("DELETE", `marketing/signups/${id}`));

export const usePitches = (companyId: string) =>
  useQuery({ queryKey: ["mk-pitches", companyId], queryFn: () => api<{ pitches: PitchRow[] }>("GET", "marketing/pitches") });
export const useAnswerPitch = () =>
  useSet("mk-pitches", (v: { id: string; status: PitchStatus }) =>
    api<{ pitches: PitchRow[] }>("PATCH", `marketing/pitches/${v.id}`, { status: v.status }),
  );
